import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, money, formatApiError } from "@/lib/api";
import { LayoutDashboard, Building2, Users2, FileCheck2, Wallet, Landmark, ClipboardList, LogOut, Plus, Trash2, Shield, UserPlus } from "lucide-react";

export function AdminLayout({ children, active }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const STAFF_ROLES = ["admin", "sales_team", "property_uploader", "accounts"];
  if (!user || user === false || !STAFF_ROLES.includes(user.role)) return null; // ProtectedRoute handles it
  const role = user.role;
  const allLinks = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, id: "overview", roles: STAFF_ROLES },
    { to: "/admin/properties", label: "Properties", icon: Building2, id: "properties", roles: ["admin", "property_uploader"] },
    { to: "/admin/investors", label: "Investors", icon: Users2, id: "investors", roles: ["admin", "sales_team"] },
    { to: "/admin/kyc", label: "KYC Queue", icon: FileCheck2, id: "kyc", roles: ["admin", "sales_team"] },
    { to: "/admin/orders", label: "Orders", icon: ClipboardList, id: "orders", roles: ["admin", "accounts"] },
    { to: "/admin/distributions", label: "Rental Payouts", icon: Landmark, id: "dist", roles: ["admin", "accounts"] },
    { to: "/admin/withdrawals", label: "Withdrawals", icon: Wallet, id: "wd", roles: ["admin", "accounts"] },
    { to: "/admin/team", label: "Team & Roles", icon: Shield, id: "team", roles: ["admin"] },
    { to: "/admin/audit", label: "Audit Logs", icon: ClipboardList, id: "audit", roles: ["admin"] },
  ];
  const links = allLinks.filter((l) => l.roles.includes(role));
  const roleLabel = {
    admin: "Admin", sales_team: "Sales", property_uploader: "Uploader", accounts: "Accounts",
  }[role] || role;
  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex">
      <aside className="hidden lg:flex flex-col w-72 bg-[#0B192C] border-r border-white/5 p-6">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-md bg-white/5 border border-white/10 flex items-center justify-center"><Building2 className="h-5 w-5 text-[#C5A059]" /></div>
          <div><div className="font-serif text-xl">OwnNest</div><div className="overline text-[#C5A059]">Staff Console</div></div>
        </Link>
        <div className="mb-6 pb-6 border-b border-white/10">
          <div className="text-xs text-white/60 truncate">{user.name}</div>
          <div className="text-[10px] text-white/40 truncate">{user.email}</div>
          <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest bg-[#C5A059]/20 text-[#C5A059]" data-testid="staff-role-badge">
            {roleLabel}
          </span>
        </div>
        <nav className="space-y-1 flex-1">
          {links.map((l) => (
            <Link key={l.id} to={l.to} data-testid={`admin-sidebar-${l.id}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${active === l.id ? "bg-[#C5A059] text-[#0B192C]" : "text-white/70 hover:bg-white/5"}`}>
              <l.icon className="h-4 w-4" />{l.label}
            </Link>
          ))}
        </nav>
        <button onClick={() => { logout(); nav("/"); }} className="mt-4 flex items-center gap-2 text-sm text-white/60 hover:text-white" data-testid="admin-logout">
          <LogOut className="h-4 w-4" />Sign out
        </button>
      </aside>
      <main className="flex-1 p-6 lg:p-10 overflow-auto">{children}</main>
    </div>
  );
}

export function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  const kpis = d ? [
    { l: "Total Investors", v: d.total_investors, tid: "kpi-investors" },
    { l: "Total Investment", v: money(d.total_investment), tid: "kpi-investment" },
    { l: "Properties", v: d.total_properties, tid: "kpi-properties" },
    { l: "Units Sold", v: d.total_units_sold.toLocaleString(), tid: "kpi-units" },
    { l: "Rental Paid Out", v: money(d.total_rental_paid), tid: "kpi-rental" },
    { l: "Referral Commissions", v: money(d.total_referral_commissions), tid: "kpi-ref" },
    { l: "Pending KYC", v: d.pending_kyc, tid: "kpi-pending-kyc" },
    { l: "Pending Withdrawals", v: d.pending_withdrawals, tid: "kpi-pending-wd" },
  ] : [];
  return (
    <AdminLayout active="overview">
      <div className="overline text-[#C5A059]">Command Center</div>
      <h1 className="font-serif text-4xl mt-2">Platform overview.</h1>
      <div className="grid md:grid-cols-4 gap-4 mt-8">
        {kpis.map((k) => (
          <div key={k.l} className="bg-[#111827] border border-white/5 rounded-xl p-6" data-testid={k.tid}>
            <div className="overline text-[#C5A059]">{k.l}</div>
            <div className="metric-value text-3xl mt-2 text-white">{k.v}</div>
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}

const emptyProp = {
  title: "", location: "", city: "", country: "US", description: "", property_type: "Residential",
  total_value: 1000000, total_units: 1000, price_per_unit: 1000, currency: "INR", assured_rental_yield: 8, projected_appreciation: 6,
  lease_term_years: 5, occupancy_status: "Leased", hero_image: "", gallery: [], amenities: [], highlights: [], status: "active",
};

const CURRENCY_OPTIONS = ["USD", "EUR", "AUD", "CAD", "INR"];

export function AdminProperties() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyProp);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const load = async () => { const { data } = await api.get("/properties"); setItems(data); };
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      total_value: parseFloat(form.total_value), total_units: parseInt(form.total_units),
      price_per_unit: parseFloat(form.price_per_unit), assured_rental_yield: parseFloat(form.assured_rental_yield),
      projected_appreciation: parseFloat(form.projected_appreciation), lease_term_years: parseInt(form.lease_term_years),
      gallery: typeof form.gallery === "string" ? form.gallery.split("\n").filter(Boolean) : form.gallery,
      amenities: typeof form.amenities === "string" ? form.amenities.split(",").map((x) => x.trim()).filter(Boolean) : form.amenities,
      highlights: typeof form.highlights === "string" ? form.highlights.split("\n").filter(Boolean) : form.highlights,
    };
    try {
      if (editingId) await api.put(`/admin/properties/${editingId}`, payload);
      else await api.post("/admin/properties", payload);
      toast.success("Saved"); setOpen(false); setEditingId(null); setForm(emptyProp); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const editProp = (p) => {
    setForm({ ...p, gallery: (p.gallery || []).join("\n"), amenities: (p.amenities || []).join(", "), highlights: (p.highlights || []).join("\n") });
    setEditingId(p.id); setOpen(true);
  };
  const del = async (id) => { if (!window.confirm("Delete property?")) return; await api.delete(`/admin/properties/${id}`); load(); };
  return (
    <AdminLayout active="properties">
      <div className="flex justify-between items-end mb-6">
        <div><div className="overline text-[#C5A059]">Assets</div><h1 className="font-serif text-4xl mt-2">Properties</h1></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-gold rounded-full" onClick={() => { setForm(emptyProp); setEditingId(null); }} data-testid="admin-add-property"><Plus className="h-4 w-4 mr-2" />Add property</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto text-slate-900">
            <DialogHeader><DialogTitle>{editingId ? "Edit" : "New"} property</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              {[
                ["title", "Title"], ["location", "Location"], ["city", "City"], ["country", "Country (ISO)"],
                ["property_type", "Property type"], ["hero_image", "Hero image URL"], ["occupancy_status", "Occupancy status"],
              ].map(([k, l]) => (<div key={k}><Label>{l}</Label><Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required data-testid={`prop-${k}`} /></div>))}
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required data-testid="prop-description" /></div>
              <div className="grid grid-cols-3 gap-3">
                {[["total_value", "Total value"], ["total_units", "Total units"], ["price_per_unit", "Price/unit"], ["assured_rental_yield", "Yield %"], ["projected_appreciation", "Apprec. %"], ["lease_term_years", "Lease yrs"]].map(([k, l]) => (
                  <div key={k}><Label>{l}</Label><Input type="number" step="0.01" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} required data-testid={`prop-${k}`} /></div>
                ))}
              </div>
              <div><Label>Gallery (one URL per line)</Label><Textarea rows={3} value={form.gallery} onChange={(e) => setForm({ ...form, gallery: e.target.value })} data-testid="prop-gallery" /></div>
              <div><Label>Amenities (comma separated)</Label><Input value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} data-testid="prop-amenities" /></div>
              <div><Label>Highlights (one per line)</Label><Textarea rows={3} value={form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} data-testid="prop-highlights" /></div>
              <Button type="submit" className="btn-gold rounded-full w-full" data-testid="prop-save">Save</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5"><tr><th className="p-3 text-left">Title</th><th className="p-3 text-left">Location</th><th className="p-3 text-center">Currency</th><th className="p-3 text-right">Value</th><th className="p-3 text-right">Yield</th><th className="p-3 text-right">Funded</th><th className="p-3"></th></tr></thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-white/5" data-testid={`admin-prop-${p.id}`}>
                <td className="p-3">{p.title}</td><td className="p-3 text-white/60">{p.location}</td>
                <td className="p-3 text-center"><span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/10">{p.currency || "INR"}</span></td>
                <td className="p-3 text-right font-mono">{money(p.total_value, p.currency)}</td>
                <td className="p-3 text-right font-mono">{p.assured_rental_yield}%</td>
                <td className="p-3 text-right font-mono">{p.funded_percent}%</td>
                <td className="p-3 flex gap-2 justify-end">
                  <Button size="sm" variant="outline" onClick={() => editProp(p)} data-testid={`admin-prop-edit-${p.id}`}>Edit</Button>
                  <Button size="sm" variant="destructive" onClick={() => del(p.id)} data-testid={`admin-prop-delete-${p.id}`}><Trash2 className="h-3 w-3" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

export function AdminInvestors() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/investors").then((r) => setItems(r.data)); }, []);
  return (
    <AdminLayout active="investors">
      <div className="overline text-[#C5A059]">CRM</div><h1 className="font-serif text-4xl mt-2">Investors</h1>
      <div className="mt-6 bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5"><tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Email</th><th className="p-3">KYC</th><th className="p-3 text-right">Invested</th><th className="p-3 text-right">Holdings</th><th className="p-3 text-right">Wallet</th></tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.user_id} className="border-t border-white/5" data-testid={`admin-inv-${u.user_id}`}>
                <td className="p-3">{u.name}</td><td className="p-3 text-white/60">{u.email}</td>
                <td className="p-3 text-center"><Badge variant={u.kyc_status === "approved" ? "default" : "secondary"}>{u.kyc_status}</Badge></td>
                <td className="p-3 text-right font-mono">{money(u.total_invested)}</td>
                <td className="p-3 text-right font-mono">{u.holdings_count}</td>
                <td className="p-3 text-right font-mono">{money(u.wallet_balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

export function AdminKyc() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const load = () => api.get(`/admin/kyc/queue?status=${statusFilter}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [statusFilter]);
  const decide = async (uid, decision) => {
    await api.post(`/admin/kyc/${uid}/decision`, { decision, note: "" });
    toast.success(`KYC ${decision}`); load();
  };
  return (
    <AdminLayout active="kyc">
      <div className="flex items-end justify-between mb-6">
        <div><div className="overline text-[#C5A059]">Compliance</div><h1 className="font-serif text-4xl mt-2">KYC Queue</h1></div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-[#111827] border border-white/10 rounded-md h-10 px-3 text-sm" data-testid="kyc-filter">
          <option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
        </select>
      </div>
      <div className="space-y-4">
        {items.map((it) => (
          <Card key={it.user.user_id} className="bg-[#111827] border-white/5 text-slate-100" data-testid={`kyc-row-${it.user.user_id}`}>
            <CardContent className="pt-6 grid md:grid-cols-3 gap-4">
              <div><div className="overline text-[#C5A059]">Investor</div><div className="mt-2 font-medium">{it.user.name}</div><div className="text-sm text-white/60">{it.user.email}</div></div>
              <div><div className="overline text-[#C5A059]">Documents</div><div className="mt-2 text-sm">{it.kyc?.full_name || "—"}<br /><span className="text-white/60">{it.kyc?.id_type} · {it.kyc?.id_number}</span><br />{it.documents.length} file(s) uploaded</div></div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => decide(it.user.user_id, "rejected")} data-testid={`kyc-reject-${it.user.user_id}`}>Reject</Button>
                <Button className="btn-gold" onClick={() => decide(it.user.user_id, "approved")} data-testid={`kyc-approve-${it.user.user_id}`}>Approve</Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="text-center text-white/50 py-16">No {statusFilter} KYC submissions.</div>}
      </div>
    </AdminLayout>
  );
}

export function AdminOrders() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setItems(r.data)); }, []);
  return (
    <AdminLayout active="orders">
      <div className="overline text-[#C5A059]">Escrow</div><h1 className="font-serif text-4xl mt-2">Investment Orders</h1>
      <div className="mt-6 bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5"><tr><th className="p-3 text-left">Order</th><th className="p-3 text-left">Property</th><th className="p-3 text-right">Units</th><th className="p-3 text-right">Amount</th><th className="p-3">Status</th><th className="p-3 text-left">Date</th></tr></thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} className="border-t border-white/5"><td className="p-3 font-mono text-xs">{o.id}</td><td className="p-3">{o.property_title}</td><td className="p-3 text-right font-mono">{o.units}</td><td className="p-3 text-right font-mono">{money(o.amount)}</td><td className="p-3 text-center"><Badge variant={o.payment_status === "paid" ? "default" : "secondary"}>{o.payment_status}</Badge></td><td className="p-3 text-white/60">{new Date(o.created_at).toLocaleDateString()}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

export function AdminDistributions() {
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState({ property_id: "", period: "", total_amount: "" });
  const load = () => api.get("/admin/rental-distributions").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/properties").then((r) => setProperties(r.data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/rental-distributions", { ...form, total_amount: parseFloat(form.total_amount) });
      toast.success("Distribution created"); setForm({ property_id: "", period: "", total_amount: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <AdminLayout active="dist">
      <div className="overline text-[#C5A059]">Payouts</div><h1 className="font-serif text-4xl mt-2">Rental Distributions</h1>
      <Card className="mt-6 bg-[#111827] border-white/5 text-slate-100 max-w-2xl"><CardContent className="pt-6">
        <form onSubmit={submit} className="grid md:grid-cols-3 gap-3 items-end">
          <div>
            <Label>Property</Label>
            <select className="w-full h-10 bg-transparent border border-white/10 rounded-md px-2 text-sm" value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })} required data-testid="dist-property">
              <option value="">Select…</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>
          <div><Label>Period (YYYY-MM)</Label><Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="2026-02" required data-testid="dist-period" /></div>
          <div><Label>Total amount</Label><Input type="number" step="0.01" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} required data-testid="dist-amount" /></div>
          <Button type="submit" className="btn-gold rounded-full md:col-span-3" data-testid="dist-submit">Distribute pro-rata</Button>
        </form>
      </CardContent></Card>
      <div className="mt-6 bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Property</th><th className="p-3">Period</th><th className="p-3 text-right">Amount</th></tr></thead>
          <tbody>
            {items.map((d) => (<tr key={d.id} className="border-t border-white/5"><td className="p-3 text-white/60">{new Date(d.created_at).toLocaleDateString()}</td><td className="p-3">{d.property_title}</td><td className="p-3 text-center font-mono">{d.period}</td><td className="p-3 text-right font-mono">{money(d.amount)}</td></tr>))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

export function AdminWithdrawals() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/withdrawals").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => {
    try {
      await api.post(`/admin/withdrawals/${id}/decision`, { decision });
      toast.success(`Withdrawal ${decision}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <AdminLayout active="wd">
      <div className="overline text-[#C5A059]">Cashout</div><h1 className="font-serif text-4xl mt-2">Withdrawals</h1>
      <div className="mt-6 bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">User</th><th className="p-3 text-right">Amount</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.id} className="border-t border-white/5" data-testid={`admin-wd-${w.id}`}>
                <td className="p-3 text-white/60">{new Date(w.created_at).toLocaleDateString()}</td>
                <td className="p-3 font-mono text-xs">{w.user_id}</td>
                <td className="p-3 text-right font-mono">{money(w.amount)}</td>
                <td className="p-3 text-center"><Badge>{w.status}</Badge></td>
                <td className="p-3 text-right">
                  {w.status === "pending" && (<div className="flex gap-2 justify-end"><Button size="sm" variant="outline" onClick={() => decide(w.id, "rejected")} data-testid={`admin-wd-reject-${w.id}`}>Reject</Button><Button size="sm" className="btn-gold" onClick={() => decide(w.id, "approved")} data-testid={`admin-wd-approve-${w.id}`}>Approve</Button></div>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}

export function AdminAudit() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setItems(r.data)); }, []);
  return (
    <AdminLayout active="audit">
      <div className="overline text-[#C5A059]">Activity</div><h1 className="font-serif text-4xl mt-2">Audit Logs</h1>
      <div className="mt-6 bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5"><tr><th className="p-3 text-left">Time</th><th className="p-3 text-left">Actor</th><th className="p-3 text-left">Action</th><th className="p-3 text-left">Target</th></tr></thead>
          <tbody>
            {items.map((l) => (<tr key={l.id} className="border-t border-white/5"><td className="p-3 text-white/60">{new Date(l.at).toLocaleString()}</td><td className="p-3 font-mono text-xs">{l.actor}</td><td className="p-3">{l.action}</td><td className="p-3 font-mono text-xs">{l.target}</td></tr>))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
